// @flow

import React from 'react';
import styled from 'styled-components';
import type { Node } from 'react';
import { absolute, Color, flex, Width, topBottomMargin, bottomPadding, topPadding } from 'styles';

const FIRST_COL_WIDTH = 20;
const FIRST_COL_WIDTH_XS = 45;
const FIRST_COL_WIDTH_MD = 38;

const ROW_MARGIN_SMALL = 4;
const ROW_MARGIN = 8;

const Wrapper = styled.div`
  width: 100%;
  ${topBottomMargin([24, 24], [48, 48], [0, 0])};
  > div {
    position: relative;
  }
`;

const Backgrounds = styled.div`
  ${absolute(0, 0, 0, `${FIRST_COL_WIDTH}%`)};
  ${flex('space-between', 'center')};

  @media print, screen and (min-width: ${Width.BREAK_XS}px) {
    left: ${FIRST_COL_WIDTH_XS}%;
  }

  @media print, screen and (min-width: ${Width.BREAK_MD}px) {
    left: ${FIRST_COL_WIDTH_MD}%;
  }

  > div {
    width: calc(50% - ${ROW_MARGIN_SMALL}px / 2);
    height: 100%;
    background: ${Color.BLURPLE_LIGHT};

    @media print, screen and (min-width: ${Width.BREAK_MD}px) {
      width: calc(50% - ${ROW_MARGIN}px / 2);
    }

    &:nth-of-type(1) {
      background: linear-gradient(180deg, rgba(161, 174, 229, 0.5) 0%, rgba(255, 255, 255, 0) 100%), #290d6d;
      border-top-left-radius: 4px;
      border-bottom-left-radius: 4px;
    }

    &:last-of-type {
      border-top-right-radius: 4px;
      border-bottom-right-radius: 4px;
    }
  }
`;

const TableWrapper = styled.div`
  max-width: 557px;
  margin: 0 auto;
`;

const HeaderRow = styled.div`
  position: relative;
  z-index: 1;
  ${flex('space-between', 'center')};
`;

const Row = styled.div`
  position: relative;
  z-index: 1;
  ${flex('space-between', 'center')};

  &:nth-of-type(2) {
    ${topPadding(12, 24, 16)};
  }

  &:last-of-type {
    ${bottomPadding(12, 24, 20)};
  }
`;

const Cell = styled.div`
  flex: 0 0 auto;
  width: calc((${100 - FIRST_COL_WIDTH}% - ${ROW_MARGIN_SMALL}px) / 2);
  margin: 15px 0;
  ${flex()};
  font-size: 1.3rem;
  line-height: calc(16 / 13);
  display: block;
  text-align: center;
  white-space: pre-line;
  box-sizing: border-box;

  @media print, screen and (min-width: ${Width.BREAK_XS}px) {
    width: calc((${100 - FIRST_COL_WIDTH_XS}% - ${ROW_MARGIN_SMALL}px) / 2);
  }

  @media print, screen and (min-width: ${Width.BREAK_MD}px) {
    width: calc((${100 - FIRST_COL_WIDTH_MD}% - ${ROW_MARGIN}px) / 2);
  }

  &:first-of-type {
    width: ${FIRST_COL_WIDTH}%;
    text-align: right;
    padding-right: 10px;

    @media print, screen and (min-width: ${Width.BREAK_XS}px) {
      width: ${FIRST_COL_WIDTH_XS}%;
    }

    @media print, screen and (min-width: ${Width.BREAK_MD}px) {
      width: ${FIRST_COL_WIDTH_MD}%;
      text-align: right;
      padding-right: 24px;
    }
  }
  &:nth-of-type(2) {
    color: ${Color.WHITE};
    margin-right: ${ROW_MARGIN_SMALL}px;

    sup {
      color: ${Color.WHITE};
    }

    @media print, screen and (min-width: ${Width.BREAK_MD}px) {
      margin-right: ${ROW_MARGIN}px;
    }
  }

  @media print, screen and (min-width: ${Width.BREAK_MD}px) {
    white-space: unset;
    padding: 0 20px;
  }
`;

const Image = styled.div`
  svg {
    width: 62px;

    path {
      fill: ${Color.PURPLE_FIGURE};
    }
  }
`;

type Props = {
  headings: Array<Node | string>,
  titles: any[],
  rows: Array<Node | string>,
  buttons: Node[],
};

const ThreeColTable = ({ headings, titles, rows, buttons, ...rest }: Props) => (
  <Wrapper className="table-wrapper" role="table" {...rest}>
    <TableWrapper role="rowgroup">
      <HeaderRow role="row">
        {titles.map((title, index) => (
          <Cell key={`TableTitle-${index}`} role="columnheader">
            {title && title.images ? <>{title.images && <Image>{title.images}</Image>}</> : title}
          </Cell>
        ))}
      </HeaderRow>
    </TableWrapper>
    <TableWrapper role="rowgroup">
      <Backgrounds>
        <div />
        <div />
      </Backgrounds>
      {rows.map((row, index) => (
        <Row role="row" key={`TableRow-${index}`}>
          {row.map((rowItem, itemIndex) => (
            <Cell role="cell" key={`TableRowItem-${itemIndex}`}>
              {rowItem}
            </Cell>
          ))}
        </Row>
      ))}
    </TableWrapper>
  </Wrapper>
);

export default ThreeColTable;
